import React, { useEffect, useState } from 'react';

type Tone = 'blue' | 'amber' | 'pink' | 'green';

interface InfoBannerProps {
  title: string;
  icon?: string;
  tone?: Tone;
  steps?: string[];
  children?: React.ReactNode;
  cta?: { label: string; onClick: () => void };
  /** When set, shows a dismiss "×" and remembers the dismissal in localStorage. */
  dismissKey?: string;
}

const TONES: Record<Tone, { bg: string; border: string; title: string; body: string; chip: string; btn: string }> = {
  blue:  { bg: 'bg-blue-50',  border: 'border-blue-200',  title: 'text-blue-900',  body: 'text-blue-800/80',  chip: 'bg-blue-500',  btn: 'bg-blue-500 hover:bg-blue-600' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', title: 'text-amber-900', body: 'text-amber-800/80', chip: 'bg-amber-500', btn: 'bg-amber-500 hover:bg-amber-600' },
  pink:  { bg: 'bg-pink-50',  border: 'border-pink-200',  title: 'text-pink-900',  body: 'text-pink-800/80',  chip: 'bg-pink-500',  btn: 'bg-pink-500 hover:bg-pink-600' },
  green: { bg: 'bg-green-50', border: 'border-green-200', title: 'text-green-900', body: 'text-green-800/80', chip: 'bg-green-500', btn: 'bg-green-500 hover:bg-green-600' },
};

const InfoBanner: React.FC<InfoBannerProps> = ({ title, icon = '💡', tone = 'blue', steps, children, cta, dismissKey }) => {
  const storageKey = dismissKey ? `infobanner.dismissed.${dismissKey}` : null;
  const [dismissed, setDismissed] = useState(() => (storageKey ? localStorage.getItem(storageKey) === '1' : false));
  // dismissKey can arrive after mount (e.g. once async data loads); re-sync when it changes.
  useEffect(() => {
    if (storageKey) setDismissed(localStorage.getItem(storageKey) === '1');
  }, [storageKey]);
  if (dismissed) return null;
  const t = TONES[tone];

  const dismiss = () => {
    if (storageKey) localStorage.setItem(storageKey, '1');
    setDismissed(true);
  };

  return (
    <div className={`relative rounded-2xl border ${t.bg} ${t.border} p-4 shadow-sm`}>
      {storageKey && (
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className={`absolute top-2.5 right-3 text-lg leading-none ${t.body} hover:opacity-70`}
        >
          ×
        </button>
      )}
      <div className="flex gap-3">
        <span className="text-2xl leading-none shrink-0">{icon}</span>
        <div className="flex-1 min-w-0">
          <p className={`font-display font-bold ${t.title} pr-5`}>{title}</p>
          {children && <div className={`text-sm mt-1 ${t.body}`}>{children}</div>}
          {steps && steps.length > 0 && (
            <ol className="mt-2 space-y-1.5">
              {steps.map((s, i) => (
                <li key={i} className={`flex gap-2 text-sm ${t.body}`}>
                  <span className={`shrink-0 w-5 h-5 rounded-full ${t.chip} text-white text-[11px] font-bold flex items-center justify-center`}>
                    {i + 1}
                  </span>
                  <span className="leading-relaxed">{s}</span>
                </li>
              ))}
            </ol>
          )}
          {cta && (
            <button
              onClick={cta.onClick}
              className={`mt-3 ${t.btn} text-white text-sm font-bold px-4 py-2 rounded-full transition`}
            >
              {cta.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfoBanner;
